import { API_ENDPOINTS } from "../config/api";

export interface HistoricalPoint {
  date: string;
  close: number;
  open?: number;
  high?: number;
  low?: number;
  volume?: number;
}

export interface MarketAsset {
  symbol: string;
  name: string;
  category: "commodities" | "indices" | "forex" | "crypto";
  flag?: string;
  unit?: string;
  price: number;
  change: number;
  changePercent: number;
  currency: string;
  open?: number;
  high?: number;
  low?: number;
  previousClose?: number;
  sparkline?: number[];
  secondaryInfo?: string;
  marketStatus?: string;
  source?: string;
  fetchedAt?: string;
}

export interface MarketResponse {
  success: boolean;
  data: {
    commodities: MarketAsset[];
    indices: MarketAsset[];
    forex: MarketAsset[];
    crypto: MarketAsset[];
  };
  summary: {
    totalTracked: number;
    topGainer?: { symbol: string; name: string; changePercent: number } | null;
    topLoser?: { symbol: string; name: string; changePercent: number } | null;
  };
  lastUpdated: string;
}

export interface HistoryResponse {
  success: boolean;
  data: {
    symbol: string;
    name: string;
    category: string;
    currency: string;
    unit?: string;
    currentPrice: number;
    change: number;
    changePercent: number;
    high?: number;
    low?: number;
    previousClose?: number;
    periodStats: {
      range: string;
      startPrice: number;
      endPrice: number;
      periodHigh: number;
      periodLow: number;
      periodChange: number;
      periodReturnPercent: number;
    };
    analysis: string;
    series: HistoricalPoint[];
  };
}

export async function fetchAllMarkets(): Promise<MarketResponse> {
  const res = await fetch(API_ENDPOINTS.MARKETS.ALL, {
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch market data: ${res.statusText}`);
  }
  return res.json();
}

export async function fetchMarketHistory(symbol: string, range: "7d" | "30d" | "90d" | "all" = "30d"): Promise<HistoryResponse> {
  const url = `${API_ENDPOINTS.MARKETS.HISTORY(symbol)}?range=${range}`;
  const res = await fetch(url, {
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch history for ${symbol}: ${res.statusText}`);
  }
  return res.json();
}

export function formatCurrencyValue(value: number, currency = "USD", decimals?: number): string {
  if (value === undefined || value === null || isNaN(value)) return "—";

  let d = decimals;
  if (d === undefined) {
    if (Math.abs(value) >= 1000) d = 2;
    else if (Math.abs(value) >= 1) d = 2;
    else d = 4;
  }

  const formattedNum = value.toLocaleString(undefined, {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });

  switch (currency.toUpperCase()) {
    case "USD":
      return `$${formattedNum}`;
    case "INR":
      return `₹${formattedNum}`;
    case "EUR":
      return `€${formattedNum}`;
    case "GBP":
      return `£${formattedNum}`;
    case "JPY":
      return `¥${formattedNum}`;
    case "HKD":
      return `HK$${formattedNum}`;
    default:
      return `${formattedNum} ${currency}`;
  }
}
